import clsx from 'clsx';
import { useRef } from 'react';
import { useHoverDirty } from 'react-use';

import { Asset } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { isAssetCollection } from '~modules/media-library/model/AssetFactory';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';
import { TeaserActions } from '~modules/media-library/view/Teaser/TeaserActions';
import { TeaserCheckbox } from '~modules/media-library/view/Teaser/TeaserCheckbox';
import { TeaserFigure } from '~modules/media-library/view/Teaser/TeaserFigure';
import { TeaserText } from '~modules/media-library/view/Teaser/TeaserText';
import { TeaserTitle } from '~modules/media-library/view/Teaser/TeaserTitle';

type TeaserProps = Resolve<{
  asset: Asset;
  AssetActions: AssetActions;
  variant?: ViewVariant;
}>;

export function Teaser({ asset, variant = ViewVariant.DEFAULT, AssetActions }: TeaserProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(ref);

  // Derived values
  const assetId = asset.id;
  const title = asset.attributes.title;
  const isCollection = isAssetCollection(asset);
  const showTeaserCheckbox = asset.attributes.workflowStatus === 'draft';

  return (
    <div
      ref={ref}
      className={clsx(
        'ml--teaser',
        `ml--teaser-variant-${variant}`,
        isCollection && 'ml--teaser-collection',
        isCollection ? 'bg-gradient-collection text-white' : 'bg-white text-black',
        'relative rounded-md transition shadow-none hover:shadow-lg w-full',
        variant === ViewVariant.TILE_LARGE && 'p-2',
        variant !== ViewVariant.LIST && 'flex flex-col flex-grow flex-shrink-0',
        variant === ViewVariant.LIST && 'w-full h-14 flex items-center',
        Boolean(AssetActions.onDetails) && 'cursor-pointer',
      )}
      title={title}
      onClick={() => AssetActions.onDetails?.(assetId)}
      data-assetid={assetId}
    >
      <TeaserFigure asset={asset} AssetActions={AssetActions} variant={variant} isHovering={isHovering} />
      <TeaserTitle asset={asset} variant={variant} />
      <div className="flex">
        <TeaserText asset={asset} variant={variant} />
        <TeaserActions asset={asset} AssetActions={AssetActions} isHovering={isHovering} variant={variant} />
        {showTeaserCheckbox && <TeaserCheckbox assetId={assetId} isHovering={isHovering} />}
      </div>
    </div>
  );
}
